import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Search, XCircle } from 'lucide-react';
import FixLink from '../../../../components/ui/FixLink';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import { Card, CardContent } from '../../../../components/ui/Card';
import { SifEmployeeIssue, WpsCoverageEmployeeView, sifEmployeeIssueFixPath } from '../../../../models/payroll';
import { HoldBackForm } from './HoldBackForm';
import { useWpsLabels } from './wpsLabels';

interface MonthRef {
  year: number;
  month: number;
}

interface IssuesPanelProps extends MonthRef {
  issues: SifEmployeeIssue[];
  holdBackEnabled: boolean;
  onChanged: () => void;
}

/** One row per person, however many checks they fail. Fix and Hold back are both on the row, so a person never stops the file. */
export const IssuesPanel: React.FC<IssuesPanelProps> = ({ year, month, issues, holdBackEnabled, onChanged }) => {
  const { t } = useTranslation();
  const labels = useWpsLabels();
  const [openFor, setOpenFor] = useState<string | null>(null);

  const people = useMemo(() => {
    const byUser = new Map<string, { userId: string; name: string; issues: SifEmployeeIssue[] }>();
    issues.forEach(issue => {
      const row = byUser.get(issue.userId) ?? { userId: issue.userId, name: issue.employeeName, issues: [] };
      row.issues.push(issue);
      byUser.set(issue.userId, row);
    });
    return [...byUser.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [issues]);

  if (people.length === 0) return null;

  return (
    <div id="employee-issues" className="scroll-mt-24" data-testid="wps-issues">
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold">{t('payroll.wps.uae.issues.title')}</h3>
          <p className="text-xs text-muted-foreground mb-2">{t('payroll.wps.uae.issues.subtitle')}</p>
          <ul className="divide-y divide-border">
            {people.map(person => (
              <li key={person.userId} className="py-3" data-testid="wps-issue-row" data-employee={person.name}>
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-medium text-foreground">{person.name}</p>
                    {person.issues.map(issue => {
                      const fixTo = sifEmployeeIssueFixPath(issue.issue, issue.userId);
                      const Icon = issue.isWarning ? AlertTriangle : XCircle;
                      return (
                        <p key={issue.issue} className="flex flex-wrap items-center gap-x-2 text-sm" data-issue={issue.issue}>
                          <Icon
                            className={`w-3.5 h-3.5 shrink-0 ${issue.isWarning ? 'text-amber-500' : 'text-red-500'}`}
                            aria-hidden="true"
                          />
                          <span className={issue.isWarning ? 'text-amber-800 dark:text-amber-200' : 'text-red-700 dark:text-red-300'}>
                            {labels.issue(issue.issue)}
                          </span>
                          {fixTo && <FixLink to={fixTo} testId="wps-issue-fix" />}
                        </p>
                      );
                    })}
                  </div>
                  {holdBackEnabled && openFor !== person.userId && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setOpenFor(person.userId)}
                      data-testid="wps-hold-back"
                    >
                      {t('payroll.wps.uae.hold.action')}
                    </Button>
                  )}
                </div>
                {openFor === person.userId && (
                  <HoldBackForm
                    year={year}
                    month={month}
                    userId={person.userId}
                    employeeName={person.name}
                    onDone={() => {
                      setOpenFor(null);
                      onChanged();
                    }}
                    onCancel={() => setOpenFor(null)}
                  />
                )}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

interface HoldAnotherEmployeeProps extends MonthRef {
  people: WpsCoverageEmployeeView[];
  onChanged: () => void;
}

const SEARCH_LIMIT = 8;

/** A small link under the issues, for someone with nothing wrong on paper. Only people who can still be held are offered. */
export const HoldAnotherEmployee: React.FC<HoldAnotherEmployeeProps> = ({ year, month, people, onChanged }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<WpsCoverageEmployeeView | null>(null);

  const candidates = useMemo(() => people.filter(p => p.state === 'ToFile' || p.state === 'NoPayroll'), [people]);
  const needle = query.trim().toLowerCase();
  const matches = needle
    ? candidates.filter(p => p.employeeName.toLowerCase().includes(needle) || (p.staffNumber ?? '').toLowerCase().includes(needle))
    : candidates;

  if (!open) {
    if (candidates.length === 0) return null;
    return (
      <button
        type="button"
        className="self-start text-sm font-medium text-primary hover:underline"
        onClick={() => setOpen(true)}
        data-testid="wps-hold-another"
      >
        {t('payroll.wps.uae.holdAnother.link')}
      </button>
    );
  }

  const close = () => {
    setOpen(false);
    setQuery('');
    setPicked(null);
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="wps-hold-another-panel">
      {picked ? (
        <>
          <p className="text-sm font-medium text-foreground">{picked.employeeName}</p>
          <HoldBackForm
            year={year}
            month={month}
            userId={picked.userId}
            employeeName={picked.employeeName}
            onDone={() => {
              close();
              onChanged();
            }}
            onCancel={() => setPicked(null)}
          />
        </>
      ) : (
        <>
          <div className="flex items-end gap-2">
            <div className="relative flex-1 min-w-0">
              <Input
                label={t('payroll.wps.uae.holdAnother.search')}
                value={query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                data-testid="wps-hold-another-search"
              />
              <Search className="w-4 h-4 absolute end-3 bottom-3 text-muted-foreground pointer-events-none" aria-hidden="true" />
            </div>
            <Button size="sm" variant="outline" onClick={close}>
              {t('common.cancel', 'Cancel')}
            </Button>
          </div>
          {matches.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">{t('payroll.wps.uae.holdAnother.noMatch')}</p>
          ) : (
            <ul className="mt-2 divide-y divide-border">
              {matches.slice(0, SEARCH_LIMIT).map(person => (
                <li key={person.userId}>
                  <button
                    type="button"
                    className="w-full flex items-center justify-between gap-2 py-2 text-start text-sm hover:text-primary"
                    onClick={() => setPicked(person)}
                    data-testid="wps-hold-another-option"
                  >
                    <span className="min-w-0 truncate">{person.employeeName}</span>
                    {person.staffNumber && <span className="text-xs text-muted-foreground shrink-0">{person.staffNumber}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {matches.length > SEARCH_LIMIT && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t('payroll.wps.uae.holdAnother.more', { n: matches.length - SEARCH_LIMIT })}
            </p>
          )}
        </>
      )}
    </div>
  );
};
